import moment from 'moment';
import { ObjectComparatorService } from './object-comparator.service';

type ObjectType = Record<string, any>;

export class ObjectDifferencesService {
  static getObjectsDiff<T extends ObjectType>(
    prevObj: T,
    curObj: T,
    reqKeys?: Array<keyof T>,
  ): ObjectType {
    const neededKeys = reqKeys
      ? reqKeys.reduce(
          (allKeys: Partial<T>, key: keyof T) => ({
            ...allKeys,
            [key]: curObj[key],
          }),
          {},
        )
      : {};

    return Object.keys(curObj).reduce((diff: ObjectType, key: string) => {
      switch (true) {
        case prevObj[key] === curObj[key]:
          return {
            ...neededKeys,
            ...diff,
          };
        case prevObj[key] instanceof moment:
          return {
            ...neededKeys,
            ...diff,
            [key]: !ObjectComparatorService.dateCompare(
              prevObj[key],
              curObj[key],
            )
              ? curObj[key]
              : undefined,
          };
        case Array.isArray(prevObj[key]):
          return this.getArraysDiff(prevObj[key], curObj[key]);
        case ObjectComparatorService.isObjects(prevObj[key], curObj[key]):
          return ObjectDifferencesService.getObjectsDiff(
            prevObj[key],
            curObj[key],
          );
        default:
          return {
            ...neededKeys,
            ...diff,
            [key]: curObj[key],
          };
      }
    }, {});
  }

  static getArraysDiff<T>(
    arr1: T[],
    arr2: T[],
    reqKeys?: Array<keyof T>,
  ): ObjectType[] {
    const arr1Sorted = [...arr1].sort();
    const arr2Sorted = [...arr2].sort();

    return [
      ...(arr1Sorted.length === arr2Sorted.length
        ? arr1Sorted
            .map(
              (sortedArrayItem: T, index: number): ObjectType =>
                ObjectComparatorService.isObjects(
                  sortedArrayItem,
                  arr2Sorted[index],
                )
                  ? this.getObjectsDiff(
                      sortedArrayItem,
                      arr2Sorted[index],
                      reqKeys,
                    )
                  : {},
            )
            .filter(
              (sortedArrayItem: ObjectType) =>
                Object.keys(sortedArrayItem).length,
            )
        : []),
    ];
  }
}
